import React, { FC, memo, useCallback, useState } from 'react';
import { useTranslate } from '@/translations';
import { useCreateDocumentType } from '@/api/DocumentManagement';
import { App, Checkbox, Form, Input, Modal, Select } from 'antd';
import { useAppCustomers } from '@/hooks';
import { AddDocumentTypeModalProps } from './types';
import { Title } from './AddDocumentTypeModal.styled';

export const AddDocumentTypeModal: FC<AddDocumentTypeModalProps> = memo(
  ({ isOpen, closeModal }) => {
    const { t } = useTranslate();
    const { message } = App.useApp();
    const {
      customerIds,
      userPermissions: { customers },
    } = useAppCustomers();
    const { mutateAsync: createDocumentType, isPending } =
      useCreateDocumentType();

    const [customer, setCustomer] = useState<number>(
      customerIds.length ? customerIds[0] : 0,
    );
    const [documentTypeCode, setDocumentTypeCode] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [isCustomerPreviewRestricted, setIsCustomerPreviewRestricted] =
      useState(false);

    const onCancel = useCallback(() => {
      setDocumentTypeCode('');
      setDocumentType('');
      setIsCustomerPreviewRestricted(false);
      closeModal();
    }, [closeModal]);

    const handleFormSave = useCallback(async () => {
      if (!documentTypeCode || !documentType || !customer) {
        message.error(
          `${t('customer')}, ${t('documentTypeCode')} and ${t(
            'documentType',
          )} fields are required`,
          10,
        );

        return;
      }

      const res = await createDocumentType({
        customerId: customer,
        documentTypeCode,
        documentType,
        isCustomerPreviewRestricted,
      });
      if (!res.success) {
        message.error(res.message.Message, 10);
      } else {
        onCancel();
      }
    }, [
      documentTypeCode,
      documentType,
      customer,
      createDocumentType,
      isCustomerPreviewRestricted,
      message,
      t,
      onCancel,
    ]);

    return (
      <Modal
        open={isOpen}
        onCancel={onCancel}
        okText={t('saveChanges')}
        onOk={handleFormSave}
        confirmLoading={isPending}
        footer={(_, { OkBtn }) => <OkBtn />}
      >
        <Title>{t('addNewDocumentType')}</Title>

        <Form size="large" labelCol={{ flex: '0 0 160px' }} labelWrap>
          {customerIds.length > 0 ? (
            <Form.Item label={t('customer')}>
              <Select
                options={customers?.map((c) => ({
                  label: c.customerName,
                  value: c.customerId,
                }))}
                value={customer}
                onChange={setCustomer}
              />
            </Form.Item>
          ) : null}
          <Form.Item label={t('documentTypeCode')}>
            <Input
              value={documentTypeCode}
              onChange={(e) => setDocumentTypeCode(e.target.value)}
            />
          </Form.Item>
          <Form.Item label={t('documentType')}>
            <Input
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            />
          </Form.Item>
          <Form.Item label={t('isCustomerPreviewRestricted')}>
            <Checkbox
              checked={isCustomerPreviewRestricted}
              onChange={(e) => setIsCustomerPreviewRestricted(e.target.checked)}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  },
);
